// ============================================
// REFERENCES PAGE - i18n
// ============================================

import { createFileRoute } from '@tanstack/react-router';
import { useState, useMemo } from 'react';
import { Plus, Trash2, Edit2, User, Building, Phone, Mail, Loader2, Users } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as Dialog from '@radix-ui/react-dialog';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getApiClient } from '@crew-at-sea/api-client';
import toast from 'react-hot-toast';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/profile/references')({
  component: ReferencesPage,
});

interface Reference {
  id: string;
  fullName: string;
  company?: string;
  position?: string;
  phone?: string;
  email?: string;
  createdAt: string;
}

function ReferencesPage() {
  const { t } = useTranslation(['panel', 'common']);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReference, setEditingReference] = useState<Reference | null>(null);
  const queryClient = useQueryClient();

  // Zod schema - t() ile çeviri desteği
  const referenceSchema = useMemo(() => z.object({
    fullName: z.string().min(2, t('references.validation.fullName')),
    company: z.string().optional(),
    position: z.string().optional(),
    phone: z.string().optional(),
    email: z.string().email(t('references.validation.email')).optional().or(z.literal('')),
  }), [t]);

  type ReferenceFormData = z.infer<typeof referenceSchema>;

  const { data: referencesResponse, isLoading } = useQuery({
    queryKey: ['references'],
    queryFn: () => getApiClient().get<Reference[]>('/references'),
  });

  const references: Reference[] = (referencesResponse as any)?.data || referencesResponse || [];

  const createMutation = useMutation({
    mutationFn: (data: ReferenceFormData) => getApiClient().post('/references', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['references'] });
      setIsModalOpen(false);
      toast.success(t('references.toast.added'));
      reset();
    },
    onError: () => {
      toast.error(t('references.toast.addError'));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: ReferenceFormData }) =>
      getApiClient().patch(`/references/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['references'] });
      setIsModalOpen(false);
      setEditingReference(null);
      toast.success(t('references.toast.updated'));
      reset();
    },
    onError: () => {
      toast.error(t('references.toast.updateError'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => getApiClient().delete(`/references/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['references'] });
      toast.success(t('references.toast.deleted'));
    },
    onError: () => {
      toast.error(t('references.toast.deleteError'));
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ReferenceFormData>({
    resolver: zodResolver(referenceSchema),
    values: editingReference
      ? {
          fullName: editingReference.fullName,
          company: editingReference.company || '',
          position: editingReference.position || '',
          phone: editingReference.phone || '',
          email: editingReference.email || '',
        }
      : undefined,
  });

  const onSubmit = (data: ReferenceFormData) => {
    const cleanData = {
      ...data,
      email: data.email || undefined,
    };

    if (editingReference) {
      updateMutation.mutate({ id: editingReference.id, data: cleanData });
    } else {
      createMutation.mutate(cleanData);
    }
  };

  const handleEdit = (reference: Reference) => {
    setEditingReference(reference);
    setIsModalOpen(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm(t('references.confirm.delete'))) {
      deleteMutation.mutate(id);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingReference(null);
    reset();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-maritime-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('references.title')}</h1>
          <p className="text-gray-500 mt-1">{t('references.subtitle')}</p>
        </div>
        <Dialog.Root open={isModalOpen} onOpenChange={setIsModalOpen}>
          <Dialog.Trigger asChild>
            <button className="btn-primary" onClick={() => setEditingReference(null)}>
              <Plus className="w-4 h-4" />
              {t('references.addReference')}
            </button>
          </Dialog.Trigger>
          <Dialog.Portal>
            <Dialog.Overlay className="fixed inset-0 bg-black/50 z-50" />
            <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-xl shadow-xl w-full max-w-md z-50 p-6">
              <Dialog.Title className="text-lg font-semibold text-gray-900 mb-4">
                {editingReference ? t('references.modal.editTitle') : t('references.modal.addTitle')}
              </Dialog.Title>
              <Dialog.Description className="sr-only">
                {editingReference ? t('references.modal.editDescription') : t('references.modal.addDescription')}
              </Dialog.Description>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                <div>
                  <label className="label">{t('references.form.fullName')} *</label>
                  <input
                    type="text"
                    className={`input ${errors.fullName ? 'input-error' : ''}`}
                    placeholder={t('references.form.fullNamePlaceholder')}
                    {...register('fullName')}
                  />
                  {errors.fullName && (
                    <p className="text-red-500 text-sm mt-1">{errors.fullName.message}</p>
                  )}
                </div>

                <div>
                  <label className="label">{t('references.form.company')}</label>
                  <input
                    type="text"
                    className="input"
                    placeholder={t('references.form.companyPlaceholder')}
                    {...register('company')}
                  />
                </div>

                <div>
                  <label className="label">{t('references.form.position')}</label>
                  <input
                    type="text"
                    className="input"
                    placeholder={t('references.form.positionPlaceholder')}
                    {...register('position')}
                  />
                </div>

                <div>
                  <label className="label">{t('references.form.phone')}</label>
                  <input
                    type="tel"
                    className="input"
                    placeholder={t('references.form.phonePlaceholder')}
                    {...register('phone')}
                  />
                </div>

                <div>
                  <label className="label">{t('references.form.email')}</label>
                  <input
                    type="email"
                    className={`input ${errors.email ? 'input-error' : ''}`}
                    placeholder={t('references.form.emailPlaceholder')}
                    {...register('email')}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                  )}
                </div>

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                  <button type="button" className="btn-secondary" onClick={handleCloseModal}>
                    {t('common:cancel')}
                  </button>
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={createMutation.isPending || updateMutation.isPending}
                  >
                    {(createMutation.isPending || updateMutation.isPending) ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : editingReference ? (
                      t('common:update')
                    ) : (
                      t('common:add')
                    )}
                  </button>
                </div>
              </form>
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      </div>

      {/* References List */}
      {references.length === 0 ? (
        <div className="card">
          <div className="card-body text-center py-12">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">{t('references.empty.title')}</h3>
            <p className="text-gray-500 mb-4">
              {t('references.empty.description')}
            </p>
            <button
              className="btn-primary mx-auto"
              onClick={() => setIsModalOpen(true)}
            >
              <Plus className="w-4 h-4" />
              {t('references.empty.addFirst')}
            </button>
          </div>
        </div>
      ) : (
        <div className="grid gap-4">
          {references.map((reference) => (
            <div key={reference.id} className="card">
              <div className="card-body">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-maritime-100 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6 text-maritime-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{reference.fullName}</h3>
                      {reference.position && (
                        <p className="text-sm text-gray-600">{reference.position}</p>
                      )}
                      {reference.company && (
                        <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
                          <Building className="w-4 h-4" />
                          {reference.company}
                        </div>
                      )}
                      <div className="flex flex-wrap gap-4 mt-2">
                        {reference.phone && (
                          <a
                            href={`tel:${reference.phone}`}
                            className="flex items-center gap-1 text-sm text-maritime-600 hover:underline"
                          >
                            <Phone className="w-4 h-4" />
                            {reference.phone}
                          </a>
                        )}
                        {reference.email && (
                          <a
                            href={`mailto:${reference.email}`}
                            className="flex items-center gap-1 text-sm text-maritime-600 hover:underline"
                          >
                            <Mail className="w-4 h-4" />
                            {reference.email}
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(reference)}
                      className="p-2 text-gray-400 hover:text-maritime-600 hover:bg-maritime-50 rounded-lg transition-colors"
                      title={t('common:edit')}
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(reference.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={t('common:delete')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Card */}
      <div className="card bg-blue-50 border-blue-200">
        <div className="card-body">
          <h4 className="font-medium text-blue-900 mb-2">💡 {t('references.tips.title')}</h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• {t('references.tips.tip1')}</li>
            <li>• {t('references.tips.tip2')}</li>
            <li>• {t('references.tips.tip3')}</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
